/**
 * Engine puro de orquestração.
 *
 * Recebe uma lista de agendamentos + timestamp atual + última execução,
 * devolve quais agendamentos devem disparar AGORA. Não toca em DOM, fetch,
 * file system, React — 100% testável.
 */

import { proximaExecucao, temAtrasado } from "./calendar";

/**
 * Para cada agendamento ativo, decide se deve disparar.
 *
 * Regras:
 *  - `pausarTodos` global ativo → ninguém dispara.
 *  - Agendamento `ativo === false` → não dispara.
 *  - `proximaExecucao` calculada a partir de `ultimaExecucao` (ou agora se nunca executado).
 *    Se já é hora ou passou, dispara.
 *  - Atrasados (app fechado): se houver atrasado, dispara UMA VEZ (deduplicação).
 *
 * @param {Object} config { pausarTodos, agendamentos }
 * @param {Date} agora
 * @returns {Array<{ agendamento, motivo: "horario" | "atrasado" }>}
 */
export function decidirDisparos(config, agora = new Date()) {
  if (!config || config.pausarTodos) return [];
  const out = [];
  for (const ag of config.agendamentos || []) {
    if (!ag.ativo) continue;
    // ISO corrompido no localStorage vira Invalid Date (truthy!): sem o
    // guard, NaN <= agora da false e o agendamento para de disparar em
    // silencio. Tratamos como nunca-executado — dispara como atrasado e a
    // execução regrava um ISO válido (auto-cura).
    let ultima = ag.ultimaExecucao ? new Date(ag.ultimaExecucao) : null;
    if (ultima && Number.isNaN(ultima.getTime())) ultima = null;
    // Quando nunca executou, a referência é a CRIAÇÃO (limitada a 24h atrás
    // para o catch-up de app fechado). Usar só "agora - 24h" fabricava uma
    // ocorrência anterior à criação: todo agendamento novo disparava na hora
    // em que era criado, mesmo com o horário-alvo ainda no futuro.
    // criadoEm corrompido vira NaN, e Math.max(NaN, x) = NaN — reintroduzindo
    // o Invalid Date silencioso que os guards acima fecham. Cai para 0
    // (= comportamento anterior: catch-up de 24h).
    const criadoEmTs = new Date(ag.criadoEm || 0).getTime();
    const base = Number.isFinite(criadoEmTs) ? criadoEmTs : 0;
    const referencia =
      ultima || new Date(Math.max(base, agora.getTime() - 24 * 60 * 60 * 1000));
    const proxima = proximaExecucao(ag.frequencia, referencia);
    if (!proxima) continue;

    if (proxima.getTime() <= agora.getTime()) {
      // Horário chegou (ou passou — atrasado).
      out.push({
        agendamento: ag,
        motivo: temAtrasado(ag.frequencia, ag.ultimaExecucao, agora) ? "atrasado" : "horario",
      });
    }
  }
  return out;
}

/**
 * Cria um novo agendamento com defaults consistentes.
 */
export function criarAgendamentoVazio() {
  return {
    id: gerarId(),
    nome: "",
    paineis: [],
    formatos: ["pdf"],
    frequencia: { tipo: "diario", hora: 8, minuto: 0 },
    subpasta: "",
    ativo: true,
    ocultarPrecos: false,
    criadoEm: new Date().toISOString(),
    ultimaExecucao: null,
    proximaExecucao: null,
  };
}

export function gerarId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ag_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Recalcula `proximaExecucao` de cada agendamento (cache para UI).
 */
export function reconciliarProximas(agendamentos, agora = new Date()) {
  return agendamentos.map((ag) => {
    if (!ag.ativo) return { ...ag, proximaExecucao: null };
    let referencia = ag.ultimaExecucao ? new Date(ag.ultimaExecucao) : agora;
    if (Number.isNaN(referencia.getTime())) referencia = agora;
    const next = proximaExecucao(ag.frequencia, referencia);
    // Invalid Date é truthy — sem o isFinite, um dado corrompido fazia
    // toISOString() lançar e derrubava criar/editar/pausar TODOS os
    // agendamentos (persistir passa por aqui com a lista inteira).
    return {
      ...ag,
      proximaExecucao: Number.isFinite(next?.getTime()) ? next.toISOString() : null,
    };
  });
}
